import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import EscritorioSelect from '../selects/EscritorioSelect';

type TipoClienteSorteador = 'todos' | 'pf' | 'pj';

interface SorteadorConfig {
  id: number;
  quantidade_clientes: number;
  auto_pay_fatura: boolean;
  send_whatsapp_msg: boolean;
  aceita_inadimplente: boolean;
  qtde_dias_vencido?: number;
  tipo_cliente: TipoClienteSorteador;
  escritorio_ids?: number[] | null;
}

interface SorteadorConfigModalProps {
  open: boolean;
  onClose: () => void;
  config: SorteadorConfig | null;
  onSave: (config: SorteadorConfig) => void;
}

const SorteadorConfigModal: React.FC<SorteadorConfigModalProps> = ({
  open,
  onClose,
  config,
  onSave,
}) => {
  const [formData, setFormData] = useState<SorteadorConfig>({
    id: 0,
    quantidade_clientes: 1,
    auto_pay_fatura: true,
    send_whatsapp_msg: true,
    aceita_inadimplente: false,
    qtde_dias_vencido: 30,
    tipo_cliente: 'todos',
    escritorio_ids: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        ...config,
        tipo_cliente: config.tipo_cliente || 'todos',
        escritorio_ids: Array.isArray(config.escritorio_ids) ? config.escritorio_ids : [],
      });
    }
  }, [config]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.quantidade_clientes < 1) {
      newErrors.quantidade_clientes = 'Quantidade deve ser maior que zero';
    }

    if (formData.quantidade_clientes > 100) {
      newErrors.quantidade_clientes = 'Quantidade máxima é 100 clientes';
    }

    if (formData.aceita_inadimplente) {
      if (!formData.qtde_dias_vencido || formData.qtde_dias_vencido < 1) {
        newErrors.qtde_dias_vencido = 'Quantidade de dias deve ser maior que zero';
      }
      
      if (formData.qtde_dias_vencido && formData.qtde_dias_vencido > 365) {
        newErrors.qtde_dias_vencido = 'Quantidade máxima é 365 dias';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof SorteadorConfig, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro do campo quando alterado
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleCheckboxChange = (field: keyof SorteadorConfig, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked,
    }));

    // Se desmarcar aceita_inadimplente, limpar qtde_dias_vencido
    if (field === 'aceita_inadimplente' && !checked) {
      setFormData(prev => ({
        ...prev,
        qtde_dias_vencido: undefined,
      }));
    }

    // Limpar erro do campo quando alterado
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <SettingsIcon />
        Configurações do Sorteador
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Quantidade de Clientes */}
          <Grid item xs={12} sm={6} md={3} sx={{ mt: 1, mb: 1 }}>
              <TextField
                fullWidth
                label="Quantidade de Clientes"
                type="number"
                size="small"
                value={formData.quantidade_clientes}
                onChange={(e) => handleInputChange('quantidade_clientes', parseInt(e.target.value) || 1)}
                error={!!errors.quantidade_clientes}                
                inputProps={{ min: 1, max: 100 }}
                variant="outlined"
              />
          </Grid>

          <Grid item xs={12} sm={6} md={4} sx={{ mt: 1, mb: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Cliente</InputLabel>
                <Select
                  label="Tipo Cliente"
                  value={formData.tipo_cliente}
                  onChange={(e) => handleInputChange('tipo_cliente', e.target.value as TipoClienteSorteador)}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pf">Pessoa Fí­sica</MenuItem>
                  <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                </Select>
              </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={5} sx={{ mt: 1, mb: 1 }}>
              <EscritorioSelect
                label="Escritórios"
                size="small"
                multiple
                showTodosOption
                value={formData.escritorio_ids || []}
                onChange={(value) => {
                  handleInputChange('escritorio_ids', Array.isArray(value) ? value : []);
                }}
                placeholder="Todos os escritórios"                
              />
          </Grid>

          {/* Checkboxes principais */}
          <Grid item xs={12} sm={6} md={6}>            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.auto_pay_fatura}
                  onChange={(e) => handleCheckboxChange('auto_pay_fatura', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Auto Pagar Fatura
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Baixar mensalidade automaticamente para clientes sorteados
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.send_whatsapp_msg}
                  onChange={(e) => handleCheckboxChange('send_whatsapp_msg', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enviar Notificação WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Notificar clientes sorteados via WhatsApp sobre a premiação
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ color: 'warning.main' }}>
              Configurações de Elegibilidade
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.aceita_inadimplente}
                  onChange={(e) => handleCheckboxChange('aceita_inadimplente', e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Aceitar Clientes Inadimplentes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Permitir que clientes com máximo 1 fatura vencida participem do sorteio
                  </Typography>
                </Box>
              }
            />

            {formData.aceita_inadimplente && (
              <Box sx={{ mt: 2, ml: 4 }}>
                <TextField
                  fullWidth
                  label="Quantidade de Dias Vencido"
                  type="number"
                  value={formData.qtde_dias_vencido || ''}
                  onChange={(e) => handleInputChange('qtde_dias_vencido', parseInt(e.target.value) || undefined)}
                  error={!!errors.qtde_dias_vencido}
                  helperText={errors.qtde_dias_vencido || 'Máximo de dias em que a fatura está vencida'}
                  inputProps={{ min: 1, max: 365 }}
                  variant="outlined"
                  size="small"
                  sx={{ maxWidth: 300 }}
                />
              </Box>
            )}
          </Grid>

          {/* Informações adicionais */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Regras do Sorteio:</strong> Serão sorteados apenas clientes com status ativo e sem faturas vencidas ou parcialmente pagas. 
                {formData.aceita_inadimplente && formData.qtde_dias_vencido && 
                  ` Se aceitar inadimplentes, clientes com até 1 fatura vencida em no máximo ${formData.qtde_dias_vencido} dias também poderão participar.`
                }
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          startIcon={<CloseIcon />}
          variant="outlined"
        >
          Cancelar
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          variant="contained"
          color="primary"
        >
          {isLoading ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SorteadorConfigModal;
